<template>
  <a
    class="cursor-pointer transition-all hover:opacity-80 w-[670px]"
    @click="handleCardClick"
  >
    <!-- 第一行：纯文字内容 -->
    <p
      class="mb-[20px] font-['BR_Hendrix',sans-serif] font-medium text-[34px] leading-[150%] tracking-[-0.02em] text-[#161616] line-clamp-3"
    >
      {{ content }}
    </p>

    <!-- 第二行：股票名称、涨跌幅、更新时间、已读标签 -->
    <div class="flex items-center justify-between ">
      <!-- 左侧：股票信息 + 更新时间 -->
      <div class="flex items-center">
        <!-- 股票信息（点击股票标签不计入打卡） -->
        <div
          v-if="symbol"
          class="flex items-center h-[58px] rounded-[5px] bg-black/5 py-[6px] px-[15px]"
          @click.stop="handleStockClick"
        >
          <!-- 股票名称 -->
          <span
            class="mr-[15px] text-[28px] font-normal leading-[140%] tracking-[-0.01em] text-[#363636] max-w-[180px] truncate inline-block"
          >
            {{ name }}
          </span>

          <!-- 涨跌幅（颜色跟随客户端配置或运营默认配置） -->
          <span
            v-if="changePercent !== undefined"
            class="text-[30px] font-bold leading-[140%] tracking-[-0.02em]"
            :style="{ color: changePercentColor }"
          >
            {{ formatChangePercent(changePercent) }}
          </span>
        </div>

        <!-- 更新时间：格式为 "Updated on X/X" 或 "X月X日更新" -->
        <span
          v-if="updateTime"
          class="ml-[15px] font-['BR_Hendrix',sans-serif] font-normal leading-[140%] tracking-[-0.01em] text-black/50"
          :class="isEnglish ? 'text-[24px]' : 'text-[26px]'"
        >
          {{ formatUpdateTime(updateTime) }}
        </span>
      </div>

      <!-- 右侧：已读标签 -->
      <ViewedBadge
        v-if="viewed"
        variant="inline"
      />
    </div>
  </a>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import ViewedBadge from './ViewedBadge.vue';
import { lang } from '../utils/lang';
import $t from '../utils/i18n';
import { ELang } from '@ark/utils';
import { useRateClsByApp } from '../utils/quotes-color';
interface Props {
  materialId: string;
  content: string;
  stockId: string;
  /** 卡片跳转链接（点击卡片时使用，计入打卡） */
  cardUrl: string;
  /** 股票标签跳转链接（点击股票标签时使用，不计入打卡） */
  stockUrl: string;
  viewed: boolean;
  updateTime?: string;
  // 行情数据（由父组件从行情接口填充）
  symbol?: string;
  name?: string;
  changePercent?: number;
  /** 运营配置的默认涨跌色：0=红涨绿跌，1=绿涨红跌 */
  defaultRateCol: number;
}
const __sfc_main = {};
__sfc_main.props = {
  materialId: {
    key: "materialId",
    required: true,
    type: String
  },
  content: {
    key: "content",
    required: true,
    type: String
  },
  stockId: {
    key: "stockId",
    required: true,
    type: String
  },
  cardUrl: {
    key: "cardUrl",
    required: true,
    type: String
  },
  stockUrl: {
    key: "stockUrl",
    required: true,
    type: String
  },
  viewed: {
    key: "viewed",
    required: true,
    type: Boolean
  },
  updateTime: {
    key: "updateTime",
    required: false,
    type: String
  },
  symbol: {
    key: "symbol",
    required: false,
    type: String
  },
  name: {
    key: "name",
    required: false,
    type: String
  },
  changePercent: {
    key: "changePercent",
    required: false,
    type: Number
  },
  defaultRateCol: {
    key: "defaultRateCol",
    required: true,
    type: Number
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;

  // 获取客户端涨跌色配置（跟随 app 设置，不支持粉色会回退到红色模式）
  const rateColorCls = useRateClsByApp(true, props.defaultRateCol);

  // 语言判断
  const isEnglish = computed(() => lang === ELang.KV.EN);

  // 涨跌色配置
  const COLORS = {
    red: '#FF435F',
    green: '#00D072'
  } as const;

  /**
   * 根据涨跌色配置获取涨跌幅颜色
   * quotes-color-0: 红涨绿跌
   * quotes-color-1: 绿涨红跌
   */
  const changePercentColor = computed(() => {
    if (props.changePercent === undefined || props.changePercent === null) {
      return '';
    }
    const isUp = props.changePercent >= 0;
    const isRedUpGreenDown = rateColorCls.value === 'quotes-color-0';
    if (isRedUpGreenDown) {
      // 红涨绿跌
      return isUp ? COLORS.red : COLORS.green;
    } else {
      // 绿涨红跌
      return isUp ? COLORS.green : COLORS.red;
    }
  });

  // 多语言文案（使用 computed 确保响应式）
  const updateTemplate = computed(() => $t('DateFormat.updateTemplate'));
  const emit = __ctx.emit;
  function formatChangePercent(percent?: number): string {
    if (percent === undefined || percent === null) return '';
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  }

  /**
   * 处理卡片点击（整个卡片区域）
   * 触发 click 事件，由父组件调用进度记录接口
   */
  function handleCardClick() {
    emit('click', props.materialId, props.cardUrl);
  }

  /**
   * 处理股票标签点击
   * 直接跳转到股票链接，不计入打卡（不触发父组件的打卡逻辑）
   */
  function handleStockClick() {
    // 如果配置了股票跳转链接，直接在组件内部处理跳转
    // 这样可以确保不触发卡片的 click 事件（不计入打卡）
    if (props.stockUrl) {
      window.location.href = props.stockUrl;
    }
  }

  // 格式化更新时间（支持多语言）
  function formatUpdateTime(dateStr: string): string {
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return updateTemplate.value.replace('{month}', month).replace('{day}', day);
  }
  return {
    isEnglish,
    changePercentColor,
    formatChangePercent,
    handleCardClick,
    handleStockClick,
    formatUpdateTime
  };
};
__sfc_main.components = Object.assign({
  ViewedBadge
}, __sfc_main.components);
export default __sfc_main;
</script>

<style scoped>
/* line-clamp-3 需要保留，因为 Tailwind 的 line-clamp 在某些构建环境下可能不可用 */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
